import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, Mail, Phone } from 'lucide-react';
import s from './page.module.css';

interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, breadcrumb, actions }: PageHeaderProps) {
  return (
    <div className={s.header}>
      <div className={s.headerText}>
        {breadcrumb && <nav className={s.breadcrumb}>{breadcrumb}</nav>}
        <h1 className={s.title}>{title}</h1>
        {subtitle && <p className={s.subtitle}>{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export interface TabItem {
  to: string;
  label: string;
  end?: boolean;
}

export function Tabs({ items, label }: { items: TabItem[]; label: string }) {
  const ref = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // Puhelimessa välilehtirivi vierii sivuttain: valittu välilehti näkyviin.
  useEffect(() => {
    const nav = ref.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active) return;
    if (active.offsetLeft < nav.scrollLeft) nav.scrollLeft = active.offsetLeft;
    else if (active.offsetLeft + active.offsetWidth > nav.scrollLeft + nav.clientWidth) {
      nav.scrollLeft = active.offsetLeft + active.offsetWidth - nav.clientWidth;
    }
  }, [pathname]);
  return (
    <nav ref={ref} className={s.tabs} aria-label={label}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => `${s.tab} ${isActive ? s.tabActive : ''}`}
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export interface StatusItem {
  label: string;
  value: ReactNode;
  muted?: boolean;
}

/** Tilannerivi: tärkeimmät luvut yhdessä paneelissa. */
export function StatusStrip({ items, note }: { items: StatusItem[]; note?: string }) {
  return (
    <div>
      <div className={s.status}>
        {items.map((item) => (
          <div key={item.label} className={s.statusCell}>
            <span className={s.statusLabel}>{item.label}</span>
            <span className={`${s.statusValue} ${item.muted ? s.statusValueMuted : ''}`}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
      {note && <p className={s.statusNote}>{note}</p>}
    </div>
  );
}

export interface DetailRow {
  label: string;
  value: ReactNode;
  /** Näkyy myös vuokralaiselle: merkitään silmällä vuokranantajan näkymässä. */
  shared?: boolean;
}

export function DetailList({ rows }: { rows: DetailRow[] }) {
  const { t } = useTranslation();
  return (
    <dl className={s.details}>
      {rows.map((row) => (
        <Fragment key={row.label}>
          <dt>
            {row.label}
            {row.shared && (
              <Eye
                size={14}
                className={s.sharedIcon}
                role="img"
                aria-label={t('property.sharedWithTenant')}
              >
                <title>{t('property.sharedWithTenant')}</title>
              </Eye>
            )}
          </dt>
          <dd>{row.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** Osion otsikko, valinnainen painike oikealla ja selite otsikon alla. */
export function Section({
  title,
  action,
  note,
  children,
}: {
  title: string;
  action?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>{title}</h2>
        {action}
      </div>
      {note && <p className={s.sectionNote}>{note}</p>}
      {children}
    </section>
  );
}

/** Yhteystieto: nimi ja sen alla puhelin- ja sähköpostilinkit. */
export function ContactValue({
  name,
  phone,
  email,
  empty = '–',
}: {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  empty?: string;
}) {
  if (!name && !phone && !email) return <>{empty}</>;
  return (
    <span className={s.contact}>
      {name && <span>{name}</span>}
      {phone && (
        <a href={`tel:${phone.replace(/[\s()-]/g, '')}`} className={s.contactLink}>
          <Phone size={14} aria-hidden="true" />
          {phone}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} className={s.contactLink}>
          <Mail size={14} aria-hidden="true" />
          {email}
        </a>
      )}
    </span>
  );
}

export { s as pageStyles };
